// Aviso de versión nueva (28-oct-2026: a Miguel le pasó). El cotizador se publica varias veces al día y un navegador abierto se
// queda con la interfaz de antes. La web sabe con qué commit se construyó (__COMMIT_WEB__, vite.config.ts) y, cada pocos
// minutos y al volver a la pestaña, pregunta a /api/salud qué commit corre el servidor; si es otro, la banda «Hay una versión
// nueva del cotizador — Recargar» (componentes/version-nueva.tsx). Si la construcción no trae el commit, compara contra el
// primero que dijo el servidor al abrir (lo mismo, salvo si se publicó justo entre abrir y esa primera pregunta).
import { useEffect, useRef, useState } from 'react';
import type { RespuestaSalud } from '@cotizador/compartido';
import { llamar } from '../api/cliente.ts';

/** El commit con que se construyó esta interfaz; null fuera de la nube (desarrollo, pruebas). */
export const COMMIT_WEB: string | null = __COMMIT_WEB__.trim() || null;

/** Cada cuánto se pregunta (además de al volver a la pestaña). */
export const REVISAR_CADA_MS = 3 * 60_000;
/** Al volver a la pestaña no se pregunta si se preguntó hace menos de esto (cambiar de ventana a cada rato no es una vuelta). */
export const REVISAR_AL_VOLVER_MS = 30_000;

/** El mismo commit aunque uno venga corto (7) y el otro completo (40). */
export function mismoCommit(a: string, b: string): boolean {
  const x = a.trim().toLowerCase();
  const y = b.trim().toLowerCase();
  const n = Math.min(x.length, y.length);
  return n >= 7 && x.slice(0, n) === y.slice(0, n);
}

/** Una vuelta: con qué commit se compara (el de la construcción o, si no lo trae, el primero que dijo el servidor) y si el del
 *  servidor ya es otro. Sin commit del servidor (null: fuera de la nube) no hay con qué comparar. */
export function revisarVersion(base: string | null, delServidor: string | null): { base: string | null; nueva: boolean } {
  if (!delServidor) return { base, nueva: false };
  if (!base) return { base: delServidor, nueva: false };
  return { base, nueva: !mismoCommit(base, delServidor) };
}

export async function commitDelServidor(): Promise<string | null> {
  const s = await llamar<RespuestaSalud>('salud');
  return s.commit ?? null;
}

/** true cuando el servidor ya corre otra versión. Deja de preguntar en cuanto la encuentra (la banda no se va hasta recargar). */
export function usarVersionNueva(op: { activo?: boolean; commitWeb?: string | null; leer?: () => Promise<string | null>; cadaMs?: number } = {}): boolean {
  const { activo = true, commitWeb = COMMIT_WEB, cadaMs = REVISAR_CADA_MS } = op;
  const leer = useRef(op.leer ?? commitDelServidor);
  leer.current = op.leer ?? commitDelServidor;
  const [nueva, setNueva] = useState(false);
  useEffect(() => {
    if (!activo || nueva) return;
    let base = commitWeb;
    let vivo = true;
    let enCurso = false;
    let ultima = 0;
    const revisar = async () => {
      if (enCurso || !vivo) return;
      enCurso = true;
      ultima = Date.now();
      try {
        const r = revisarVersion(base, await leer.current());
        base = r.base;
        if (r.nueva && vivo) setNueva(true);
      } catch (e) {
        // sin red o el servidor reiniciándose (justo al publicar): se vuelve a preguntar en la siguiente vuelta
        console.warn('[versión] no se pudo preguntar la versión del servidor', e);
      } finally {
        enCurso = false;
      }
    };
    const alVolver = () => {
      if (document.visibilityState === 'visible' && Date.now() - ultima >= REVISAR_AL_VOLVER_MS) void revisar();
    };
    const id = setInterval(() => void revisar(), cadaMs);
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('focus', alVolver);
    void revisar();
    return () => {
      vivo = false;
      clearInterval(id);
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('focus', alVolver);
    };
  }, [activo, nueva, commitWeb, cadaMs]);
  return nueva;
}
